import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Progress,
  Spinner,
  Text,
  VStack,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const defaultIsPending = (item) => item.estado === 'PENDIENTE';

/**
 * Carrusel para pasar lista músico a músico.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} [props.title]
 * @param {Array<{ id: string, name: string, subtitle?: string, foto?: string|null, estado: string }>} props.items
 * @param {Array<{ key: string, label: string, color: string, icon: any }>} props.statuses
 * @param {(id: string, estado: string) => Promise<void>} props.onMark
 * @param {(item: object) => boolean} [props.isPending]
 */
export default function PasarListaModal({
  isOpen,
  onClose,
  title = 'Pasar lista',
  items = [],
  statuses = [],
  onMark,
  isPending = defaultIsPending,
}) {
  
  const [index, setIndex] = useState(0);
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [saving, setSaving] = useState(false);

  const visible = useMemo(() => {
    if (!soloPendientes) return items;
    return items.filter(isPending);
  }, [items, soloPendientes, isPending]);

  useEffect(() => {
    if (!isOpen) return;
    setIndex(0);
  }, [isOpen, soloPendientes]);

  useEffect(() => {
    if (visible.length === 0) {
      setIndex(0);
      return;
    }
    if (index >= visible.length) setIndex(visible.length - 1);
  }, [visible.length, index]);

  const current = visible[index] ?? null;
  const liveEstado = current
    ? (items.find((i) => i.id === current.id)?.estado ?? current.estado)
    : null;

  const markedCount = items.filter((i) => !isPending(i)).length;
  const progressPct = items.length ? (markedCount / items.length) * 100 : 0;

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(visible.length - 1, i + 1));

  const handleMark = async (estado) => {
    if (!current || saving) return;
    setSaving(true);
    try {
      await onMark(current.id, estado);
      const leavesPendingFilter = soloPendientes && !isPending({ ...current, estado });
      if (!leavesPendingFilter && index < visible.length - 1) {
        setIndex((i) => i + 1);
      }
      // Si sale del filtro de pendientes, el mismo índice pasa a ser el siguiente al re-render.
    } catch (err) {
      toaster.create({
        title: 'No se pudo guardar',
        description: err?.response?.data?.message || err?.message || undefined,
        type: 'error',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} size='md' placement='center' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.600" />
        <Dialog.Positioner>
          <Dialog.Content mx={3} rounded="2xl" overflow="hidden">
            <Dialog.Header pb={2}>
              <VStack align="stretch" gap={2}>
                <Flex justify="space-between" align="center" pr={6}>
                  <Text fontSize="lg" fontWeight="800">{title}</Text>
                  <Badge colorPalette="purple" fontSize="sm" px={2} py={0.5} rounded="md">
                    {markedCount}/{items.length}
                  </Badge>
                </Flex>
                <Progress.Root value={progressPct} size="sm" colorPalette="purple" rounded="full">
                  <Progress.Track>
                    <Progress.Range />
                  </Progress.Track>
                </Progress.Root>
                <Flex justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.500">
                    {soloPendientes
                      ? `${visible.length} pendiente${visible.length === 1 ? '' : 's'}`
                      : `${visible.length ? index + 1 : 0} de ${visible.length}`}
                  </Text>
                  <HStack gap={2}>
                    <Text fontSize="xs" color="gray.500">Solo pendientes</Text>
                    <Switch
                      size="sm"
                      colorPalette="purple"
                      checked={soloPendientes}
                      onChange={(e) => setSoloPendientes(e.target.checked)}
                    />
                  </HStack>
                </Flex>
              </VStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />

            <Dialog.Body py={4}>
              {!current ? (
                <VStack py={10} gap={3}>
                  <Text fontSize="2xl">✓</Text>
                  <Text fontWeight="700" textAlign="center">
                    {items.length === 0 ? 'No hay músicos en la lista' : 'Lista completada'}
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {items.length === 0
                      ? 'Añade convocados o crea la encuesta de asistencia.'
                      : soloPendientes
                        ? 'No quedan pendientes. Desactiva el filtro para revisar a todos.'
                        : 'Todos los músicos tienen un estado asignado.'}
                  </Text>
                </VStack>
              ) : (
                <VStack gap={5}>
                  <HStack w="100%" justify="space-between" align="center">
                    <IconButton
                      aria-label="Anterior"
                      variant="ghost"
                      disabled={index <= 0 || saving}
                      onClick={goPrev}><FaChevronLeft /></IconButton>
                    <VStack gap={3} flex={1}>
                      <Avatar.Root size="2xl" bg="purple.100" color="purple.700"><Avatar.Fallback name={current.name} /><Avatar.Image src={current.foto || undefined} /></Avatar.Root>
                      <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="800" lineHeight="1.2">
                          {current.name}
                        </Text>
                        {current.subtitle ? (
                          <Text fontSize="sm" color="gray.500" mt={1}>{current.subtitle}</Text>
                        ) : null}
                        {liveEstado ? (
                          <Badge
                            mt={2}
                            colorPalette={statuses.find((s) => s.key === liveEstado)?.color || 'gray'}
                          >
                            {statuses.find((s) => s.key === liveEstado)?.label || liveEstado}
                          </Badge>
                        ) : null}
                      </Box>
                    </VStack>
                    <IconButton
                      aria-label="Siguiente"
                      variant="ghost"
                      disabled={index >= visible.length - 1 || saving}
                      onClick={goNext}><FaChevronRight /></IconButton>
                  </HStack>

                  <Box
                    w="100%"
                    display="grid"
                    gridTemplateColumns="repeat(2, 1fr)"
                    gap={3}
                  >
                    {statuses.map((st) => {
                      const active = liveEstado === st.key;
                      return (
                        <Tooltip key={st.key} content={st.label} showArrow>
                          <Button
                            size="lg"
                            h="64px"
                            colorPalette={st.color}
                            variant={active ? 'solid' : 'outline'}
                            onClick={() => handleMark(st.key)}
                            disabled={saving}
                            rounded="xl"
                            fontWeight="700">{saving && active ? <Spinner size="sm" /> : <Icon boxSize={5} asChild><st.icon /></Icon>}{st.label}</Button>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </VStack>
              )}
            </Dialog.Body>

            <Dialog.Footer borderTopWidth="1px" gap={2}>
              <Button variant="ghost" onClick={onClose}>Cerrar</Button>
              {current ? (
                <Button
                  variant="outline"
                  onClick={goNext}
                  disabled={index >= visible.length - 1 || saving}
                >
                  Saltar
                </Button>
              ) : null}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
